import {COMMERCE} from '../src/commerce-types';
import {TEAM_ROLES} from '../src/team';
import {SOLUTION_PAGES} from './solutions';
import {TEAM_PAGE_PATH, teamRolePath} from './public-paths';

export interface PublicLink {path: string; label: string}
export interface PublicMenu {
  label: string;
  title: string;
  description: string;
  overview: PublicLink;
  groups: {label: string; links: readonly PublicLink[]}[];
}

/** PublicHeader and PublicFooter share these route groups. Product and role names come from their existing owners. */
export const PUBLIC_MENUS = {
  products: {
    label: 'Products', title: 'Put your ideas to work.',
    description: 'Work with your team. Explore payments and domains for what you build.',
    overview: {path: '/products', label: 'Explore all products'},
    groups: [
      {label: 'Your AI team', links: [{path: TEAM_PAGE_PATH, label: 'Meet the eight agents'}, {path: '/how-it-works', label: 'How EightBots works'}, {path: '/download', label: 'EightBots app'}]},
      {label: 'Build your business', links: COMMERCE.products.map(product => ({path: product.publicPath, label: product.name}))},
      {label: 'Payment examples', links: COMMERCE.products.filter(product => product.id !== 'domains').map(product => ({path: `${product.publicPath}/demo`, label: `${product.label} example`}))},
      {label:'Ways to pay',links:[{path:'/payment-methods',label:'All payment methods'},{path:'/payment-methods/crypto',label:'Crypto assets and networks'},{path:'/payment-methods/apple-pay',label:'Apple Pay'},{path:'/payment-methods/google-pay',label:'Google Pay'}]},
    ],
  },
  solutions: {
    label: 'Solutions', title: 'Get help with your next task.',
    description: 'Research, build, write, and plan with a team you can direct.',
    overview: {path: '/solutions', label: 'Explore all solutions'},
    groups: [
      {label: 'For your work', links: SOLUTION_PAGES.filter(page => page.path !== '/use-cases').map(page => ({path: page.path, label: page.label}))},
      {label: 'See what is possible', links: [{path: '/use-cases', label: 'Use cases'}, {path: TEAM_PAGE_PATH, label: 'Your team'}, {path: '/contact-sales', label: 'Talk to our team'}]},
    ],
  },
  team: {
    label: 'Your team', title: 'Meet the bots behind the work.',
    description: 'Give your team a goal. Each bot brings a different skill to the work.',
    overview: {path: TEAM_PAGE_PATH, label: 'Meet your team'},
    groups: [
      {label: 'Team lead', links: TEAM_ROLES.filter(role => role.id === 'head').map(role => ({path: teamRolePath(role.id), label: role.name}))},
      {label: 'Specialists', links: TEAM_ROLES.filter(role => role.id !== 'head').map(role => ({path: teamRolePath(role.id), label: role.name}))},
      {label: 'Work together', links: [{path: '/how-it-works', label: 'How it works'}, {path: '/settings?connect=whatsapp', label: 'Use WhatsApp'}]},
    ],
  },
  resources: {
    label: 'Resources', title: 'Get to know EightBots.',
    description: 'Learn how to start, follow the work, and stay connected to your team.',
    overview: {path: '/how-it-works', label: 'How it works'},
    groups: [
      {label: 'Get started', links: [{path: '/how-it-works', label: 'How it works'}, {path: '/download', label: 'Download EightBots'}, {path: '/developer', label: 'Developer'}]},
      {label: 'Company', links: [{path: '/company', label: 'About EightBots'}, {path: '/news', label: 'News'}, {path: '/contact-sales', label: 'Contact sales'}]},
      {label: 'Policies', links: [{path: '/privacy', label: 'Privacy policy'}, {path: '/terms', label: 'Terms of service'}]},
    ],
  },
} satisfies Record<string, PublicMenu>;

export type PublicMenuKey = keyof typeof PUBLIC_MENUS;
export const PUBLIC_NAVIGATION: readonly ({menu: PublicMenuKey} | PublicLink)[] = [
  {menu: 'products'}, {menu: 'solutions'}, {menu: 'team'},
  {menu: 'resources'}, {path: '/company', label: 'Company'}, {path: '/pricing', label: 'Pricing'},
];

// The footer selects from the same destinations without repeating the full menus.
function footerLinks(menu:PublicMenu,label:string){
  const group=menu.groups.find(item=>item.label===label);
  if(!group)throw new Error(`Footer navigation group is missing: ${menu.label} / ${label}`);
  return group.links;
}
export const PUBLIC_FOOTER_GROUPS = [
  {label:'Products',links:[...footerLinks(PUBLIC_MENUS.products,'Your AI team'),...COMMERCE.products.filter(product=>product.id==='domains').map(product=>({path:product.publicPath,label:product.name}))]},
  {label:PUBLIC_MENUS.team.label,links:PUBLIC_MENUS.team.groups.slice(0,2).flatMap(group=>group.links)},
  {label:PUBLIC_MENUS.solutions.label,links:[...footerLinks(PUBLIC_MENUS.solutions,'For your work'),...footerLinks(PUBLIC_MENUS.solutions,'See what is possible').filter(link=>link.path==='/use-cases')]},
  {label:PUBLIC_MENUS.resources.label,links:[...footerLinks(PUBLIC_MENUS.resources,'Company'),...footerLinks(PUBLIC_MENUS.resources,'Get started').filter(link=>link.path==='/developer'),...PUBLIC_NAVIGATION.filter((item):item is PublicLink=>'path' in item&&item.path==='/pricing')]},
] satisfies {label:string;links:readonly PublicLink[]}[];
